<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import AccountChip from '@ui/AccountChip.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import Drawer from '../lib/ui/Drawer.svelte';
  import WebhooksPanel from '../lib/ui/webhooks/WebhooksPanel.svelte';
  import SearchSelect, { type SearchOption } from '@ui/SearchSelect.svelte';
  import MemberSelect from '../lib/ui/MemberSelect.svelte';
  import { timeAgo, timeUntil } from '@ui/member';
  import {
    redirectIfAwaitingOnboarding, getToken, handle401, type AdminUser,
  } from '../lib/admin';
  import { onCommunityChange, withCommunity } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import {
    fetchAutomations, enableAutomation, disableAutomation, createAutomation,
    updateAutomation, setSender, fetchTestRecipients, sendTest,
    fetchQueue, fetchActivity, removeEnrollment, addEnrollment,
    triggerLabel, triggerClause, triggerOnset, healthCopy, testSendError, waitingText,
    slugify, parseAfter, formatAfter, DELAY_UNITS,
    type Automation, type SenderOption, type QueueItem, type ActivityItem,
  } from '../lib/automations';
  import ChangesPanel from '../lib/ui/ChangesPanel.svelte';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);
  let toast = $state<{ text: string; kind: 'info' | 'error' } | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | undefined;

  // Which tab. Auto DMs and Webhooks are both "when X happens, do Y" and share the
  // trigger vocabulary, so they belong on one page — what differs is whether the Y is a
  // message we send or a POST somebody else receives.
  // "Changes" is the third because it is about the other two: every enable, edit, sender
  // swap and trigger on this page writes a row it reads back.
  let tab = $state<'dms' | 'webhooks' | 'changes'>('dms');
  let skoolId = $state<number | null>(null);
  let items = $state<Automation[]>([]);
  let senders = $state<SenderOption[]>([]);
  let triggers = $state<string[]>([]);
  let busyKey = $state<string | null>(null);

  const anyConnected = $derived(senders.some((s) => s.connected));

  const TRIGGER_GLYPH: Record<string, string> = {
    'member.joined': '👋', 'member.cancelled': '🚪', 'member.payment_failed': '💳',
    manual: '➕',
  };
  const glyph = (t: string) => TRIGGER_GLYPH[t] ?? '✉️';

  function flash(text: string, kind: 'info' | 'error' = 'info'): void {
    toast = { text, kind };
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast = null), 3200);
  }
  function apply(updated: Automation): void {
    items = items.map((a) => (a.key === updated.key ? updated : a));
  }
  function senderAvatar(a: Automation): string | null {
    return senders.find((s) => s.external_id === a.sender.external_id)?.avatar_url ?? null;
  }

  async function load(id: number): Promise<void> {
    const data = await fetchAutomations(id);
    items = data.items;
    senders = data.senders;
    triggers = data.triggers;
  }

  async function toggle(a: Automation): Promise<void> {
    if (skoolId == null || busyKey) return;
    if (!a.enabled && !a.can_enable) {
      flash(`Connect ${a.sender.name}'s Skool account before turning this on.`, 'error');
      return;
    }
    // Turning ON a new-joiner sequence collides with anything else that DMs on join —
    // Skool's own built-in welcome DM, or a tool like Skool Panda / SkootCRM. Warn first,
    // or a new member gets two welcome messages.
    if (!a.enabled && a.trigger === 'member.joined') {
      const ok = confirm(
        'Before you turn this on:\n\n' +
        "Turn OFF Skool's built-in welcome DM (Settings → Automations) and any other " +
        'welcome-message tool you use (Skool Panda, SkootCRM, etc.).\n\n' +
        'If you leave them on, every new member gets duplicate welcome DMs — this sequence ' +
        "won't replace them, it sends on top.\n\nTurn the sequence on now?");
      if (!ok) return;
    }
    busyKey = a.key;
    try {
      const updated = a.enabled
        ? await disableAutomation(skoolId, a.key)
        : await enableAutomation(skoolId, a.key);
      apply(updated);
      flash(updated.enabled ? `${updated.name} is on` : `${updated.name} is off`);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not update the automation.', 'error');
    } finally {
      busyKey = null;
    }
  }

  // --- test send ---------------------------------------------------------------
  let testing = $state<string | null>(null);   // automation key with the test panel open
  let testRecipient = $state<string | null>(null);
  let testStep = $state(0);                     // which message of the sequence to test
  let testSending = $state(false);

  function openTest(a: Automation): void {
    testing = testing === a.key ? null : a.key;
    panel = null;
    testRecipient = null;
    testStep = 0;
  }
  function recipientSearch(a: Automation): (q: string) => Promise<SearchOption[]> {
    return async (q) => {
      if (skoolId == null) return [];
      const r = await fetchTestRecipients(skoolId, a.key, q);
      return r.items.map((x) => ({
        id: x.external_id, name: x.name, avatarUrl: x.avatar_url,
        ...(x.handle ? { subtitle: `@${x.handle}` } : {}),
      }));
    };
  }
  async function runTest(a: Automation): Promise<void> {
    if (skoolId == null || !testRecipient || testSending) return;
    testSending = true;
    try {
      const res = await sendTest(skoolId, a.key, testRecipient, testStep);
      flash(`Test DM sent to ${res.recipient_name} ✓`);
      testing = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      // The DOOR's own vocabulary, in words. Every way a send can be refused comes back
      // as a stable code; a raw message here would be whatever the server happened to
      // say, and on `send_outcome_unknown` the difference matters — that one must never
      // read "try again", because the DM may already have gone.
      flash(testSendError(err), 'error');
    } finally {
      testSending = false;
    }
  }

  async function pickSender(a: Automation, ext: string | null): Promise<void> {
    if (skoolId == null || busyKey) return;
    busyKey = a.key;
    try {
      apply(await setSender(skoolId, a.key, ext));
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not change the sender.', 'error');
    } finally {
      busyKey = null;
    }
  }

  // --- the queue + activity panel ----------------------------------------------
  // One panel open at a time; the "In sequence" tab is the live queue (with remove),
  // the "Activity" tab is the send log (who got which DM, and its exact text).
  let panel = $state<{ key: string; tab: 'queue' | 'activity' } | null>(null);
  let panelLoading = $state(false);
  let queueRows = $state<QueueItem[]>([]);
  let activityRows = $state<ActivityItem[]>([]);
  let removing = $state<string | null>(null);   // member_external_id being removed
  let addMemberId = $state<number | null>(null); // member picked to manually enrol
  let adding = $state(false);

  // The automation the drawer is open on — resolved from the live list so its stats stay
  // current as we add/remove members.
  const selected = $derived(panel ? items.find((a) => a.key === panel!.key) ?? null : null);

  async function openPanel(a: Automation, tab: 'queue' | 'activity'): Promise<void> {
    // Re-clicking the tab it's already on closes the drawer; switching tabs just reloads.
    if (panel && panel.key === a.key && panel.tab === tab) { panel = null; return; }
    testing = null;
    addMemberId = null;
    panel = { key: a.key, tab };
    await loadPanel(a);
  }
  async function loadPanel(a: Automation): Promise<void> {
    if (skoolId == null || !panel) return;
    panelLoading = true;
    queueRows = [];
    activityRows = [];
    try {
      if (panel.tab === 'queue') queueRows = (await fetchQueue(skoolId, a.key)).items;
      else activityRows = (await fetchActivity(skoolId, a.key)).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not load that.', 'error');
    } finally {
      panelLoading = false;
    }
  }
  async function removeMember(a: Automation, ext: string | null): Promise<void> {
    if (skoolId == null || !ext || removing) return;
    removing = ext;
    try {
      apply(await removeEnrollment(skoolId, a.key, ext));
      queueRows = queueRows.filter((r) => r.member_external_id !== ext);
      flash('Removed from the sequence');
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not remove that member.', 'error');
    } finally {
      removing = null;
    }
  }
  async function addMember(a: Automation): Promise<void> {
    if (skoolId == null || addMemberId == null || adding) return;
    adding = true;
    try {
      apply(await addEnrollment(skoolId, a.key, addMemberId));
      addMemberId = null;
      if (panel?.tab === 'queue') queueRows = (await fetchQueue(skoolId, a.key)).items;
      flash('Added to the sequence');
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not add that member.', 'error');
    } finally {
      adding = false;
    }
  }

  // --- the editor (create + edit share one form) -------------------------------
  type StepDraft = { n: number; unit: string; message: string };
  type Form = {
    mode: 'create' | 'edit';
    key: string | null;
    name: string;
    trigger: string;
    sender: string | null;
    origSender: string | null;
    stopOnReply: boolean;
    steps: StepDraft[];
  };
  let form = $state<Form | null>(null);
  let saving = $state(false);

  function openCreate(): void {
    form = {
      mode: 'create', key: null, name: '', trigger: 'member.joined',
      sender: null, origSender: null, stopOnReply: true,
      steps: [{ n: 0, unit: 'm', message: 'Hey {member}, welcome to {community}!' }],
    };
  }
  function openEdit(a: Automation): void {
    form = {
      mode: 'edit', key: a.key, name: a.name, trigger: a.trigger,
      sender: a.sender.external_id, origSender: a.sender.external_id,
      stopOnReply: a.stop_on.includes('member.replied'),
      steps: a.steps.map((s) => {
        const { n, unit } = parseAfter(s.after);
        return { n, unit, message: s.message };
      }),
    };
  }
  function closeForm(): void { form = null; }
  function addStep(): void {
    if (form) form.steps = [...form.steps, { n: 3, unit: 'd', message: '' }];
  }
  function removeStep(i: number): void {
    if (form) form.steps = form.steps.filter((_, idx) => idx !== i);
  }

  const canSave = $derived(
    !!form &&
    form.name.trim().length > 0 &&
    form.steps.length > 0 &&
    form.steps.every((s) => s.message.trim().length > 0));

  async function save(): Promise<void> {
    if (skoolId == null || !form || !canSave || saving) return;
    saving = true;
    const f = form;
    const steps = f.steps.map((s) => ({
      after: formatAfter(s.n, s.unit), message: s.message.trim(),
    }));
    const stop_on = f.stopOnReply ? ['member.left', 'member.replied'] : ['member.left'];
    try {
      let saved: Automation;
      if (f.mode === 'create') {
        saved = await createAutomation(skoolId, {
          key: slugify(f.name), name: f.name.trim(), trigger: f.trigger,
          steps, stop_on, sender_external_id: f.sender,
        });
        items = [...items, saved].sort((x, y) => x.key.localeCompare(y.key));
        flash(`Created “${saved.name}” — turn it on when you're ready`);
      } else {
        saved = await updateAutomation(skoolId, f.key!, { name: f.name.trim(), steps, stop_on });
        if (f.sender !== f.origSender) saved = await setSender(skoolId, f.key!, f.sender);
        apply(saved);
        flash(`Saved “${saved.name}”`);
      }
      form = null;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      flash(err instanceof Error ? err.message : 'Could not save the sequence.', 'error');
    } finally {
      saving = false;
    }
  }

  // --- lifecycle ---------------------------------------------------------------
  async function init(): Promise<void> {
    if (!getToken()) { navigate('/admin/login'); return; }
    loading = true; error = null;
    try {
      let fetchedFor: number | null = null;
      const [boot] = await Promise.all([
        ensureBootstrap(),
        withCommunity((id) => { fetchedFor = id; skoolId = id; return load(id); }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      watch.seen(fetchedFor);
    } catch (err: unknown) {
      watch.rearm();
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load automations.';
    } finally {
      loading = false;
    }
  }

  const watch = onCommunityChange((id) => void reload(id));

  async function reload(id: number): Promise<void> {
    dataLoading = true; error = null; skoolId = id; form = null;
    try {
      await load(id);
    } catch (err: unknown) {
      watch.rearm();
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load automations.';
    } finally {
      dataLoading = false;
    }
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && items.length === 0}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <PageHeader section="Automations"
                  title={tab === 'dms' ? 'Auto DMs' : tab === 'webhooks' ? 'Webhooks' : 'Changes'} />

      <div role="tablist" class="tabs tabs-bordered mb-5">
        <button role="tab" class="tab {tab === 'dms' ? 'tab-active' : ''}"
                onclick={() => (tab = 'dms')}>Auto DMs</button>
        <button role="tab" class="tab {tab === 'webhooks' ? 'tab-active' : ''}"
                onclick={() => (tab = 'webhooks')}>Webhooks</button>
        <button role="tab" class="tab {tab === 'changes' ? 'tab-active' : ''}"
                onclick={() => (tab = 'changes')}>Changes</button>
      </div>

      {#if tab === 'dms'}

      <p class="text-sm text-base-content/60 -mt-2 mb-5 max-w-2xl">
        Send a timed sequence of direct messages on autopilot — as a real person on your
        team. A sequence only runs while it's on, and it stops the moment a member replies
        or leaves.
      </p>

      {#if !anyConnected}
        <div class="alert alert-warning mb-5 text-sm">
          <span>
            <strong>No connected accounts yet.</strong> Auto DMs go out as one of your
            team's Skool accounts. Connect an account in the Sideguide Helper extension,
            then choose it as the sender below to turn a sequence on.
          </span>
        </div>
      {/if}

      {#if !form}
        <div class="mb-5">
          <button class="btn btn-primary btn-sm" onclick={openCreate}>
            + Build your own sequence
          </button>
        </div>
      {/if}

      <!-- ============================ the editor ============================ -->
      {#if form}
        <div class="card bg-base-100 ring-1 ring-primary/30 mb-6 shadow-sm">
          <div class="card-body gap-5">
            <div class="flex items-center gap-2">
              <span class="text-xl">{glyph(form.trigger)}</span>
              <h3 class="font-semibold text-base">
                {form.mode === 'edit' ? 'Edit sequence' : 'New auto-DM sequence'}
              </h3>
            </div>

            <!-- what it's called + what fires it + who sends it -->
            <div class="grid gap-4 sm:grid-cols-3">
              <label class="form-control">
                <span class="label-text text-xs font-medium text-base-content/60 mb-1">Name</span>
                <!-- privacy-ok: the operator typing a name for their own automation, not data we hold -->
                <input class="input input-bordered input-sm" bind:value={form.name}
                       placeholder="Welcome new members" />
              </label>
              <label class="form-control">
                <span class="label-text text-xs font-medium text-base-content/60 mb-1">Runs when</span>
                {#if form.mode === 'edit'}
                  <div class="input input-bordered input-sm flex items-center bg-base-200/50
                              text-base-content/70">{triggerLabel(form.trigger)}</div>
                {:else}
                  <select class="select select-bordered select-sm" bind:value={form.trigger}>
                    {#each triggers as t}<option value={t}>{triggerLabel(t)}</option>{/each}
                  </select>
                {/if}
              </label>
              <label class="form-control">
                <span class="label-text text-xs font-medium text-base-content/60 mb-1">Sends as</span>
                <select class="redact select select-bordered select-sm" bind:value={form.sender}>
                  <option value={null}>The owner (default)</option>
                  {#each senders as s}
                    <option value={s.external_id}>
                      <!-- privacy-ok: the <select> above carries the mark; a native
                           option popup cannot be blurred by CSS either way -->
                      {s.name}{s.connected ? '' : ' — not connected'}
                    </option>
                  {/each}
                </select>
              </label>
            </div>

            <!-- the messages, each with a structured delay -->
            <div class="flex flex-col gap-3">
              <div class="flex items-baseline gap-2">
                <span class="text-xs font-medium text-base-content/60">Messages</span>
                <span class="text-[0.7rem] text-base-content/45">
                  each delay is counted from when {triggerClause(form.trigger)}
                </span>
              </div>
              {#each form.steps as step, i}
                <div class="rounded-lg ring-1 ring-base-content/10 p-3 bg-base-200/30">
                  <div class="flex items-center gap-2 text-xs text-base-content/60 mb-2 flex-wrap">
                    <span class="font-semibold text-base-content/70">Message {i + 1}</span>
                    <span class="opacity-40">·</span>
                    <span>send</span>
                    <input type="number" min="0" class="input input-bordered input-xs w-16 text-center"
                           bind:value={step.n} />
                    <select class="select select-bordered select-xs" bind:value={step.unit}>
                      {#each DELAY_UNITS as u}<option value={u.v}>{u.label}</option>{/each}
                    </select>
                    <span>after {triggerClause(form.trigger)}{step.n === 0 ? ' (right away)' : ''}</span>
                    <span class="flex-1"></span>
                    {#if form.steps.length > 1}
                      <button class="btn btn-ghost btn-xs text-error" onclick={() => removeStep(i)}>
                        Remove
                      </button>
                    {/if}
                  </div>
                  <textarea class="textarea textarea-bordered textarea-sm w-full" rows="3"
                            bind:value={step.message}
                            placeholder="Write the message — use {'{member}'} for their name and {'{community}'} for the community."></textarea>
                </div>
              {/each}
              <button class="btn btn-ghost btn-xs self-start" onclick={addStep}>+ Add a message</button>
            </div>

            <label class="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" class="checkbox checkbox-sm" bind:checked={form.stopOnReply} />
              Stop the sequence if the member replies
            </label>

            <div class="flex gap-2 justify-end pt-1">
              <button class="btn btn-ghost btn-sm" onclick={closeForm}>Cancel</button>
              <button class="btn btn-primary btn-sm" disabled={!canSave || saving} onclick={save}>
                {saving ? 'Saving…' : form.mode === 'edit' ? 'Save changes' : 'Create (off)'}
              </button>
            </div>
          </div>
        </div>
      {/if}

      <!-- ============================ the list ============================ -->
      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if items.length === 0}
        <StateBlock state="empty" message="No automations yet." />
      {:else}
        <div class="flex flex-col gap-4">
          {#each items as a (a.key)}
            <div class="card bg-base-100 ring-1
                        {a.stats.failed > 0 ? 'ring-error/40' : 'ring-base-content/10'}">
              <div class="card-body gap-4 py-5">
                <!-- header: identity + edit + the on/off switch -->
                <div class="flex items-start justify-between gap-3">
                  <div class="flex items-start gap-3 min-w-0">
                    <span class="text-2xl leading-none mt-0.5">{glyph(a.trigger)}</span>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <!-- privacy-ok: the automation's own name ("Welcome DM") — the owner's config, not a member -->
                        <h3 class="font-semibold truncate">{a.name}</h3>
                        {#if a.is_builtin}<span class="badge badge-ghost badge-sm">Built-in</span>{/if}
                      </div>
                      <p class="text-xs text-base-content/50 mt-0.5">{triggerLabel(a.trigger)}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-3 shrink-0">
                    <button class="btn btn-ghost btn-xs gap-1
                              {panel?.key === a.key && panel.tab === 'queue' ? 'btn-active' : ''}"
                            onclick={() => openPanel(a, 'queue')}>
                      Queue
                      {#if a.stats.active > 0}
                        <span class="badge badge-neutral badge-xs">{a.stats.active}</span>
                      {/if}
                    </button>
                    <button class="btn btn-ghost btn-xs" onclick={() => openTest(a)}>Test send</button>
                    <button class="btn btn-ghost btn-xs" onclick={() => openEdit(a)}>Edit</button>
                    <!-- privacy-ok: a title, which private mode strips wholesale at runtime -->
                    <label class="flex items-center gap-2 cursor-pointer"
                           title={!a.enabled && !a.can_enable
                             ? `Connect ${a.sender.name}'s account first` : ''}>
                      <span class="text-xs {a.enabled ? 'text-success' : 'text-base-content/40'}">
                        {a.enabled ? 'On' : 'Off'}
                      </span>
                      <input type="checkbox" class="toggle toggle-success toggle-sm"
                             checked={a.enabled}
                             disabled={busyKey === a.key || (!a.enabled && !a.can_enable)}
                             onchange={() => toggle(a)} />
                    </label>
                  </div>
                </div>

                <!-- the sequence timeline — delays are from when they {trigger} -->
                <ol class="flex flex-col gap-2 border-l-2 border-base-content/10 pl-4 ml-1">
                  {#each a.steps as step}
                    <li class="relative">
                      <span class="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full
                                   bg-base-300 ring-2 ring-base-100"></span>
                      <span class="badge badge-neutral badge-sm font-mono"
                            title="counted from when {triggerClause(a.trigger)}">
                        {step.after === '0m'
                          ? triggerOnset(a.trigger)
                          : `${step.after} after ${triggerClause(a.trigger)}`}
                      </span>
                      <p class="text-sm text-base-content/80 mt-1 line-clamp-3">{step.message}</p>
                    </li>
                  {/each}
                </ol>

                <div class="border-t border-base-content/8 pt-3 flex flex-wrap items-center
                            justify-between gap-3">
                  <!-- sender picker -->
                  <div class="flex items-center gap-2 text-sm">
                    <span class="text-base-content/50">Sends as</span>
                    <details class="dropdown">
                      <summary class="btn btn-ghost btn-sm gap-1 normal-case">
                        <AccountChip name={a.sender.name} avatarUrl={senderAvatar(a)}
                                     size={22} connected={a.sender.connected} />
                        <span class="opacity-50">▾</span>
                      </summary>
                      <ul class="dropdown-content menu bg-base-100 rounded-box z-10 w-64
                                 p-2 shadow ring-1 ring-base-content/10">
                        <li>
                          <button onclick={() => pickSender(a, null)}>
                            <span class="text-base-content/70">The owner (default)</span>
                          </button>
                        </li>
                        {#each senders as opt}
                          <li>
                            <button onclick={() => pickSender(a, opt.external_id)}>
                              <AccountChip name={opt.name} avatarUrl={opt.avatar_url}
                                           size={20} connected={opt.connected} />
                            </button>
                          </li>
                        {/each}
                      </ul>
                    </details>
                  </div>

                  <!-- live status; the counts open the queue / activity; failed is LOUD -->
                  <div class="flex flex-wrap gap-2 text-xs">
                    {#if a.enabled || a.stats.sent > 0 || a.stats.active > 0}
                      <button class="badge badge-ghost badge-sm hover:badge-neutral
                                     {panel?.key === a.key && panel.tab === 'queue' ? 'badge-neutral' : ''}"
                              onclick={() => openPanel(a, 'queue')}>
                        {a.stats.active} in sequence
                      </button>
                      <button class="badge badge-ghost badge-sm hover:badge-neutral
                                     {panel?.key === a.key && panel.tab === 'activity' ? 'badge-neutral' : ''}"
                              onclick={() => openPanel(a, 'activity')}>
                        {a.stats.sent} sent
                      </button>
                    {/if}
                    {#if a.stats.stopped > 0}
                      <span class="badge badge-ghost badge-sm">{a.stats.stopped} stopped</span>
                    {/if}
                    {#if a.stats.failed > 0}
                      <button class="badge badge-error badge-sm gap-1 font-semibold"
                              onclick={() => openPanel(a, 'activity')}>
                        ⚠ {a.stats.failed} failed send{a.stats.failed === 1 ? '' : 's'}
                      </button>
                    {/if}
                    {#if a.stats.unknown > 0}
                      <button class="badge badge-warning badge-sm gap-1 font-semibold"
                              title="We cannot tell whether these messages arrived. Add the
                                     member to the sequence again to start their run over."
                              onclick={() => openPanel(a, 'activity')}>
                        ? {a.stats.unknown} unconfirmed
                      </button>
                    {/if}
                  </div>
                </div>

                {#if a.health && a.health.status !== 'ok'}
                  <!-- WHAT IS WRONG, THEN WHAT TO DO. `can_enable` only answers "do we
                       hold the sender's credential"; a sequence has four other ways to be
                       switched on and silent (the sender stopped being staff, their
                       session expired, we lost the members page, Skool is throttling
                       them). Each of those has a different fix, and a warning without one
                       is a warning nobody can act on — so the reason enum maps to a pair
                       of sentences and both are shown. -->
                  {@const copy = healthCopy(a.health)}
                  {@const stopped = a.health.status === 'cannot_send'}
                  <div class="rounded-lg px-3 py-2 text-xs flex gap-2 items-start
                              {stopped ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'}">
                    <span aria-hidden="true">⚠</span>
                    <div class="min-w-0">
                      <p class="font-semibold">
                        {#if stopped && a.enabled}
                          This is on, and nothing is going out.
                        {:else if stopped}
                          This cannot be turned on yet.
                        {:else}
                          This is sending, but something is wrong.
                        {/if}
                      </p>
                      <p class="opacity-90">{copy.what}</p>
                      {#if copy.fix}<p class="opacity-90">{copy.fix}</p>{/if}
                    </div>
                  </div>
                {:else if !a.enabled && !a.can_enable}
                  <p class="text-xs text-warning flex items-center gap-1">
                    ⚠ Connect <strong class="redact">{a.sender.name}</strong>'s Skool account to turn this on.
                  </p>
                {/if}

                {#if testing === a.key}
                  <div class="rounded-lg ring-1 ring-primary/25 bg-primary/[0.03] p-3 mt-1
                              flex flex-col gap-2.5">
                    <p class="text-xs text-base-content/60">
                      Send one message from this sequence now, as <strong class="redact">{a.sender.name}</strong>,
                      to someone in their Skool inbox — a one-off test that enrols nobody.
                    </p>

                    <!-- which message to test — a picker when there's more than one -->
                    {#if a.steps.length > 1}
                      <div class="flex flex-wrap gap-1.5">
                        {#each a.steps as _step, i}
                          <button class="btn btn-xs {testStep === i
                                    ? 'btn-primary' : 'btn-ghost ring-1 ring-base-content/15'}"
                                  onclick={() => (testStep = i)}>
                            Message {i + 1}
                          </button>
                        {/each}
                      </div>
                    {/if}
                    <div class="rounded bg-base-200/50 px-2.5 py-2 text-sm text-base-content/75 line-clamp-3">
                      {a.steps[testStep]?.message ?? ''}
                    </div>

                    <div class="flex flex-wrap items-center gap-2">
                      <SearchSelect
                        value={testRecipient}
                        search={recipientSearch(a)}
                        onchange={(opt) => (testRecipient = opt ? opt.id : null)}
                        placeholder="Choose a recipient"
                        searchPlaceholder="Search your inbox…"
                        emptyText="No conversations match." />
                      <button class="btn btn-primary btn-sm"
                              disabled={!testRecipient || testSending || !a.can_enable}
                              onclick={() => runTest(a)}>
                        {testSending ? 'Sending…' : `Send message ${testStep + 1}`}
                      </button>
                      <button class="btn btn-ghost btn-sm" onclick={() => (testing = null)}>Cancel</button>
                    </div>
                    {#if !a.can_enable}
                      <p class="text-xs text-warning">
                        Connect <span class="redact">{a.sender.name}</span>'s account before test-sending.
                      </p>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
      {:else if skoolId !== null && tab === 'webhooks'}
        <WebhooksPanel {skoolId} onflash={flash} />
      {:else if skoolId !== null}
        <ChangesPanel {skoolId} />
      {/if}
    </PageContainer>
  {/if}

  <!-- ===================== the queue / activity drawer ===================== -->
  {#if selected}
    {@const a = selected}
    <Drawer open={panel !== null} onclose={() => (panel = null)} label="Sequence details" scroll={false}>
      <div class="flex items-center gap-2 border-b hair px-4 py-3 shrink-0">
        <span class="text-xl">{glyph(a.trigger)}</span>
        <div class="min-w-0">
          <!-- privacy-ok: the automation's own name — the owner's config, not a member -->
          <h3 class="font-semibold truncate leading-tight">{a.name}</h3>
          <p class="text-xs text-base-content/50">{triggerLabel(a.trigger)}</p>
        </div>
        <span class="flex-1"></span>
        <button class="btn btn-ghost btn-sm btn-circle" onclick={() => (panel = null)} aria-label="Close">✕</button>
      </div>

      <!-- tabs -->
      <div class="flex items-center gap-1.5 px-4 pt-3 shrink-0">
        <button class="btn btn-xs {panel?.tab === 'queue'
                  ? 'btn-primary' : 'btn-ghost ring-1 ring-base-content/15'}"
                onclick={() => openPanel(a, 'queue')}>
          In sequence{a.stats.active > 0 ? ` (${a.stats.active})` : ''}
        </button>
        <button class="btn btn-xs {panel?.tab === 'activity'
                  ? 'btn-primary' : 'btn-ghost ring-1 ring-base-content/15'}"
                onclick={() => openPanel(a, 'activity')}>Activity</button>
      </div>

      <div class="flex-1 overflow-y-auto px-4 py-3">
        {#if panel?.tab === 'queue'}
          <!-- manually add a member — a first trigger by hand -->
          <div class="rounded-lg ring-1 ring-primary/25 bg-primary/[0.03] p-3 mb-3 flex flex-col gap-2">
            <p class="text-xs text-base-content/60">
              Add someone to this sequence by hand — it starts them from the first message,
              like a trigger you fired yourself.
            </p>
            <div class="flex items-center gap-2">
              <div class="flex-1 min-w-0">
                <MemberSelect {skoolId} bind:value={addMemberId}
                              placeholder="Search members to add…" />
              </div>
              <button class="btn btn-primary btn-sm" disabled={addMemberId == null || adding}
                      onclick={() => addMember(a)}>
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
            {#if !a.enabled}
              <p class="text-[0.7rem] text-warning">
                ⚠ This sequence is off — added members wait here until you turn it on.
              </p>
            {/if}
          </div>

          {#if panelLoading}
            <p class="text-xs text-base-content/50 py-6 text-center">Loading…</p>
          {:else if queueRows.length === 0}
            <p class="text-xs text-base-content/50 py-6 text-center">
              Nobody is in this sequence right now.
            </p>
          {:else}
            <ul class="flex flex-col divide-y divide-base-content/8">
              {#each queueRows as r (r.member_external_id)}
                <li class="flex items-center gap-2.5 py-2.5">
                  <Avatar src={r.avatar_url ?? null} name={r.member_name} size={32} />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <span class="redact text-sm font-medium truncate">{r.member_name}</span>
                      {#if r.source === 'manual'}
                        <span class="badge badge-primary badge-outline badge-xs">manual</span>
                      {/if}
                      {#if r.paused_in_conversation}
                        <span class="badge badge-warning badge-xs">paused · in a chat</span>
                      {/if}
                    </div>
                    <p class="text-[0.7rem] text-base-content/55">
                      Message {r.step_index + 1} of {r.steps_total}
                      {#if r.paused_in_conversation}
                        · waiting until they go quiet
                      {:else if !r.waiting}
                        · next {timeUntil(r.next_step_at)}
                      {/if}
                    </p>
                    {#if r.waiting}
                      <!-- A DUE DATE THAT HAS PASSED IS NOT AN ANSWER. Something is in
                           the way, and the server already knows which — the sequence's
                           own verdict when nobody here is getting anything, this
                           member's last stamped skip otherwise. Saying which keeps the
                           row from reading "next 2 hours ago". -->
                      <p class="text-[0.7rem] text-warning mt-0.5">
                        {r.waiting.scope === 'sequence' ? 'Nothing is sending' : 'Waiting'}
                        — {waitingText(r.waiting)}{#if r.waiting.reason === 'transient' && r.send_attempts}
                          {' '}(attempt {r.send_attempts} of 3){/if}{#if r.waiting.since}
                          {' '}· still true {timeAgo(r.waiting.since)}{/if}
                      </p>
                    {/if}
                  </div>
                  <button class="btn btn-ghost btn-xs text-error"
                          title="Remove from this sequence"
                          disabled={removing === r.member_external_id || !r.member_external_id}
                          onclick={() => removeMember(a, r.member_external_id)}>
                    {removing === r.member_external_id ? '…' : 'Remove'}
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        {:else}
          {#if panelLoading}
            <p class="text-xs text-base-content/50 py-6 text-center">Loading…</p>
          {:else if activityRows.length === 0}
            <p class="text-xs text-base-content/50 py-6 text-center">No messages sent yet.</p>
          {:else}
            <ul class="flex flex-col divide-y divide-base-content/8">
              {#each activityRows as r, i (r.member_external_id + '-' + r.step_index + '-' + i)}
                <li class="flex items-start gap-2.5 py-2.5">
                  <Avatar src={r.avatar_url ?? null} name={r.member_name} size={32} />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5 flex-wrap text-xs">
                      <span class="redact font-medium">{r.member_name}</span>
                      {#if r.source === 'manual'}
                        <span class="badge badge-primary badge-outline badge-xs">manual</span>
                      {/if}
                      <span class="opacity-30">·</span>
                      <span class="text-base-content/50">Message {r.step_index + 1}</span>
                      <span class="opacity-30">·</span>
                      {#if r.status === 'sent'}
                        <span class="text-success">sent {timeAgo(r.sent_at)}</span>
                      {:else if r.status === 'failed'}
                        <span class="text-error font-semibold">failed {timeAgo(r.sent_at)}</span>
                      {:else if r.status === 'unknown'}
                        <span class="text-warning font-semibold">
                          not confirmed {timeAgo(r.sent_at)}</span>
                      {:else if r.status === 'sending'}
                        <span class="text-base-content/50">sending…</span>
                      {:else}
                        <span class="text-base-content/50">{r.status}</span>
                      {/if}
                    </div>
                    {#if r.body}
                      <p class="text-sm text-base-content/80 mt-0.5 whitespace-pre-wrap
                                line-clamp-4 redact">{r.body}</p>
                    {/if}
                    {#if r.status === 'unknown'}
                      <!-- TERMINAL, AND WAITING ON A PERSON. This member's sequence is
                           stopped and will never resume on its own — we hold no message
                           id, so nothing here can prove the DM arrived, and re-sending
                           blind would risk the same message twice. The channel is the one
                           handle we do have, so it is offered as the thing to go and
                           look at. -->
                      <div class="mt-1 rounded bg-warning/10 px-2 py-1.5 text-[0.7rem]
                                  text-warning leading-snug">
                        <p>
                          We could not confirm this message was delivered. Check the
                          conversation, then remove and re-add the member to continue.
                        </p>
                        {#if r.channel_id}
                          <!-- privacy-ok: an opaque Skool thread id, not a person or
                               anything they wrote -->
                          <p class="mt-0.5 font-mono opacity-80">
                            Conversation {r.channel_id}
                          </p>
                        {/if}
                      </div>
                    {:else if r.error}
                      <p class="text-[0.7rem] text-error mt-0.5">⚠ {r.error}</p>
                    {/if}
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
      </div>
    </Drawer>
  {/if}

  {#if toast}
    <div class="toast toast-end z-50">
      <div class="alert {toast.kind === 'error' ? 'alert-error' : 'alert-info'} text-sm">
        {toast.text}
      </div>
    </div>
  {/if}
</AdminShell>
